import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Save, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const GoogleKeysForm = ({ redirectUri, onSaved }) => {
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ GOOGLE_CLIENT_ID: '', GOOGLE_CLIENT_SECRET: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/google/settings`, { withCredentials: true }).then(r => setView(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim()));
    if (!Object.keys(payload).length) { toast.error('Paste at least one value'); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/google/settings`, payload, { withCredentials: true });
      setView(r.data);
      setForm({ GOOGLE_CLIENT_ID: '', GOOGLE_CLIENT_SECRET: '' });
      toast.success(r.data.configured ? 'Google keys saved — you can connect now' : 'Saved · one key still missing');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const prodUri = redirectUri?.replace(/https:\/\/[^/]+/, 'https://tirthindustrieserp.com');
  const copy = (v) => { navigator.clipboard?.writeText(v); toast('Copied'); };

  return (
    <div className="mt-4 rounded-xl border border-dashed border-zinc-700 p-4 space-y-3" data-testid="google-keys-form">
      <div className="flex items-center gap-2 text-zinc-200 text-xs font-semibold"><KeyRound size={13} className="text-amber-400" /> Google OAuth keys (admin only · stored encrypted in the ERP, not shown to staff)</div>
      <p className="text-[11px] text-zinc-500">Google Cloud Console → APIs &amp; Services → Credentials → OAuth client (Web) → add these redirect URIs, enable <b>Google Calendar API</b> + <b>Google Tasks API</b>, then paste the Client ID &amp; Secret.</p>
      {[redirectUri, prodUri].filter((v, i, a) => v && a.indexOf(v) === i).map(u => (
        <button key={u} type="button" onClick={() => copy(u)} className="w-full flex items-center justify-between gap-2 text-[11px] font-mono text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-lg px-2.5 py-1.5 hover:border-zinc-600" data-testid="google-redirect-uri">
          <span className="truncate">{u}</span><Copy size={11} className="shrink-0 text-zinc-500" />
        </button>
      ))}
      {['GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_SECRET'].map(k => (
        <div key={k}>
          <label className="text-zinc-400 text-[11px] mb-1 flex items-center justify-between">
            <span>{k === 'GOOGLE_CLIENT_ID' ? 'Client ID' : 'Client Secret'}</span>
            {view?.keys?.[k]?.set && <span className="text-emerald-400">set · {view.keys[k].value}</span>}
          </label>
          <input
            type={k === 'GOOGLE_CLIENT_SECRET' ? 'password' : 'text'}
            value={form[k]}
            onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
            placeholder={view?.keys?.[k]?.set ? 'Paste to replace' : (k === 'GOOGLE_CLIENT_ID' ? '1234-abc.apps.googleusercontent.com' : 'GOCSPX-…')}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
            data-testid={`google-key-${k.toLowerCase()}`}
          />
        </div>
      ))}
      <button type="button" onClick={save} disabled={saving} className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold flex items-center gap-1.5 disabled:bg-zinc-700" data-testid="google-keys-save">
        <Save size={12} /> {saving ? 'Saving…' : 'Save keys'}
      </button>
    </div>
  );
};
